// Storyboard for the /about intro film: how long it runs, when each scene owns
// the screen, and the fixture data the scenes render. Kept apart from the scene
// components so the running order is readable in one place and a timing tweak
// does not mean scrolling through JSX.
//
// Scenes are laid out against a fixed 1600x900 canvas which AboutIntroOverlay
// scales to fit, so every coordinate in a scene is a raw px value on that canvas.

export const DURATION = 17;
export const STAGE_WIDTH = 1600;
export const STAGE_HEIGHT = 900;

/** [start, end] in seconds for each scene. Overlaps are deliberate cross-fades. */
export const T = {
  noise: [0, 3.6] as const,
  turn: [3.45, 6.5] as const,
  grade: [6.4, 10] as const,
  strata: [10, 12.6] as const,
  voice: [12.55, 14.7] as const,
  wordmark: [14.65, 17] as const,
};

function makeRng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

const TEMPLATE_FRAGMENTS = [
  'Hi {{first_name}},',
  'Just circling back --',
  'Did you see my last email?',
  'Quick question for you',
  'Bumping this to the top',
  'Following up re:',
  'Hope this finds you well',
  'Reposting in case you missed it',
  'Block 15 min on my calendar?',
  'Adding value:',
  '10x your pipeline',
  'Synergize',
  'AI-personalized at scale',
  'Touching base',
  'I noticed you work at {{company}}',
  "Saw you're a {{title}}",
  'Just want to make sure you saw this',
  'Mind if I send a quick intro?',
  'Wanted to check in re:',
  'Let me know your thoughts',
  'Is now a bad time?',
  'P.S. Last try - promise',
  'Per my last email',
  'Free for a quick chat?',
  "I'll keep this brief --",
  'Open to a conversation?',
  '{{Personalized hook}}',
  '{{ICEBREAKER}}',
  '- sent from my AI agent',
  'Just one more bump',
];

export interface NoiseFragment {
  id: string;
  text: string;
  x: number;
  y: number;
  size: number;
  drift: number;
  opacity: number;
  rot: number;
  delay: number;
}

// Seeded so the drifting spam layer is identical on every play and in snapshots.
export const FRAGMENTS: NoiseFragment[] = (() => {
  const rng = makeRng(2025);
  return Array.from({ length: 38 }, (_, i) => ({
    id: `fragment-${i}`,
    text: TEMPLATE_FRAGMENTS[i % TEMPLATE_FRAGMENTS.length],
    x: 40 + rng() * 1500,
    y: 60 + rng() * 800,
    size: 11 + rng() * 7,
    drift: 30 + rng() * 70,
    opacity: 0.16 + rng() * 0.32,
    rot: (rng() - 0.5) * 4,
    delay: rng() * 0.8,
  }));
})();

/**
 * The film's own tier fixtures. Deliberately NOT `@/types/grade` TIER_MAP: this
 * is illustrative marketing copy for an animation, not the product's grade
 * system, and it carries per-tier fill heights and hexes that only the intro
 * uses. Renaming a real tier should not silently restage the film.
 */
export interface StoryTier {
  key: string;
  label: string;
  short: string;
  color: string;
  fill: number;
}

export const TIERS: StoryTier[] = [
  { key: 'nugget', label: 'Inner Circle', short: 'Inner Circle', color: '#e8b84b', fill: 92 },
  { key: 'vein', label: 'High Trust', short: 'High Trust', color: '#c4983a', fill: 70 },
  { key: 'seam', label: 'Professional', short: 'Professional', color: '#9a7426', fill: 50 },
  { key: 'prospect', label: 'Acquaintance', short: 'Acquaintance', color: '#6b5c3e', fill: 24 },
];

export interface StoryContact {
  name: string;
  role: string;
  tier: number;
}

export const CONTACTS: StoryContact[] = [
  { name: 'Sarah Park', role: 'VP Product, Plate', tier: 0 },
  { name: 'Marcus Liu', role: 'Founder, Ember Labs', tier: 0 },
  { name: 'Priya Shah', role: 'Partner, North Field', tier: 1 },
  { name: 'Ben Hartley', role: 'Head of Growth, Riverbend', tier: 1 },
  { name: 'Diane Cho', role: 'PM, Atlas Systems', tier: 2 },
  { name: 'Theo Aldermann', role: 'Director, Halberd Capital', tier: 2 },
  { name: 'Ravi Mehta', role: 'Eng, Forecast', tier: 3 },
  { name: 'Joanna Ng', role: 'COO, Vellum', tier: 3 },
];

export const TIER_MARKERS = [
  { label: 'Inner Circle', color: '#e8b84b', y: 0.05, appear: 0.7 },
  { label: 'High Trust', color: '#c4983a', y: 0.3, appear: 1 },
  { label: 'Professional', color: '#9a7426', y: 0.5, appear: 1.3 },
  { label: 'Acquaintance', color: '#6b5c3e', y: 0.76, appear: 1.6 },
];

export const EMAIL_BODY =
  "Hi Sarah,\n\nYour talk last week on cohort decay stuck with me - especially the bit about week-3 retention. I'm working on something adjacent and would love your take. Coffee Thursday?";
